import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  teal: '#00B4D8', navy: '#0D1B2A', dark: '#1F2937', grey: '#6B7280',
  border: '#E5E7EB', bg: '#F0F7FA', success: '#10B981', warning: '#F59E0B',
  danger: '#EF4444', purple: '#8B5CF6',
};

const LESSON_TYPES = [
  { key: 'group',            label: 'Group Class',            icon: '👥', desc: 'Up to 12 students per session — also applies to Hybrid extra sessions' },
  { key: 'private',          label: '1-on-1 Human Tutor',     icon: '🧑‍🏫', desc: 'Private session — also applies to extra 1-on-1 bookings' },
  { key: 'ielts_specialist', label: 'IELTS Specialist',        icon: '🎯', desc: 'IELTS preparation classes' },
  { key: 'corporate',        label: 'Corporate / Business',    icon: '💼', desc: 'Business English classes' },
];

export default function AdminTutorRates() {
  const [rates, setRates]       = useState([]);
  const [edited, setEdited]     = useState({});
  const [saving, setSaving]     = useState(false);
  const [resetting, setResetting] = useState({});
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => { fetchRates(); }, []);

  const fetchRates = async () => {
    try {
      const r = await api.get('/api/admin/tutor-rates');
      setRates(r.data.rates || []);
      const init = {};
      (r.data.rates || []).forEach(rt => { init[rt.lesson_type] = rt.effective_rate; });
      setEdited(init);
    } catch (e) {
      setError('Failed to load tutor rates');
    }
  };

  const handleChange = (lt, val) => {
    setEdited(prev => ({ ...prev, [lt]: val }));
    setSaved(false); setError('');
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    const ratesPayload = {};
    LESSON_TYPES.forEach(({ key }) => { ratesPayload[key] = parseFloat(edited[key]) || 0; });
    try {
      await api.post('/api/admin/tutor-rates', { rates: ratesPayload });
      await fetchRates();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save rates');
    }
    setSaving(false);
  };

  const handleReset = async (lt) => {
    setResetting(prev => ({ ...prev, [lt]: true }));
    try {
      await api.post('/api/admin/tutor-rates/reset', { lesson_type: lt });
      await fetchRates();
    } catch (e) {
      setError(e.response?.data?.error || 'Reset failed');
    }
    setResetting(prev => ({ ...prev, [lt]: false }));
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset ALL tutor rates to platform defaults?')) return;
    setSaving(true);
    try {
      await api.post('/api/admin/tutor-rates/reset', {});
      await fetchRates();
    } catch (e) {
      setError('Reset failed');
    }
    setSaving(false);
  };

  // Cost per student estimates: based on typical sessions/month
  const sessionsPerMonth = { group: 12, private: 12, ielts_specialist: 20, corporate: 12 };

  const getRateData = (lt) => rates.find(r => r.lesson_type === lt) || {};

  return (
    <Layout role="tenant_admin">
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '28px 20px 60px' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: '0 0 6px', fontWeight: 800, fontSize: 24, color: C.dark }}>
            💰 Tutor Rates
          </h1>
          <p style={{ margin: 0, color: C.grey, fontSize: 14 }}>
            Set what you pay tutors per lesson. Changes apply to new earnings records only — historical payouts are not affected.
          </p>
        </div>

        {/* Info banner */}
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 12, padding: '14px 18px', marginBottom: 28, fontSize: 13, color: '#1E40AF' }}>
          <strong>ℹ️ How this works:</strong> These are the gross amounts tutors earn per session hour.
          Platform fees (if enabled) are deducted automatically when calculating tutor net pay.
          All rates are in USD.
        </div>

        {/* Rates table */}
        <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1.2fr auto', gap: 0, background: C.bg, padding: '12px 20px', borderBottom: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.grey }}>
            <div>LESSON TYPE</div>
            <div style={{ textAlign: 'center' }}>PLATFORM DEFAULT</div>
            <div style={{ textAlign: 'center' }}>YOUR RATE ($/hr)</div>
            <div style={{ textAlign: 'center' }}>EST. COST/STUDENT</div>
            <div></div>
          </div>

          {LESSON_TYPES.map(({ key, label, icon, desc }, i) => {
            const rd        = getRateData(key);
            const val       = edited[key] ?? rd.effective_rate ?? rd.default_rate ?? '';
            const isCustom  = rd.is_custom;
            const isChanged = parseFloat(val) !== rd.effective_rate;
            const sessions  = sessionsPerMonth[key] || 8;
            const calcSessions = { group: 12, private: 12, ielts_specialist: 20, corporate: 12 };
            const rawCost = val ? parseFloat(val) * (calcSessions[key] || 12) / 12 : null;
            const monthlyCost = rawCost !== null ? (rawCost % 1 === 0 ? Math.round(rawCost) : parseFloat(rawCost.toFixed(2))) : '—';
            const isLow     = parseFloat(val) > 0 && parseFloat(val) < 15;

            return (
              <div key={key}
                style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1.2fr auto', gap: 0, padding: '16px 20px', borderBottom: i < LESSON_TYPES.length - 1 ? `1px solid ${C.border}` : 'none', alignItems: 'center', background: isCustom ? '#FAFFF8' : '#fff' }}>

                {/* Label */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.dark }}>{label}</div>
                      <div style={{ fontSize: 12, color: C.grey }}>{desc}</div>
                    </div>
                    {isCustom && (
                      <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, marginLeft: 4 }}>Custom</span>
                    )}
                  </div>
                </div>

                {/* Default rate */}
                <div style={{ textAlign: 'center', fontSize: 14, color: C.grey, fontWeight: 600 }}>
                  ${rd.default_rate ?? '—'}
                </div>

                {/* Editable rate */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', border: `2px solid ${isChanged ? C.teal : isLow ? C.warning : C.border}`, borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                    <span style={{ padding: '8px 10px', background: C.bg, color: C.grey, fontSize: 14, fontWeight: 700, borderRight: `1px solid ${C.border}` }}>$</span>
                    <input
                      type="number"
                      min="1"
                      max="9999"
                      step="0.5"
                      value={val}
                      onChange={e => handleChange(key, e.target.value)}
                      style={{ width: 72, padding: '8px 10px', border: 'none', outline: 'none', fontSize: 15, fontWeight: 700, color: isLow ? C.warning : C.dark, textAlign: 'center' }}
                    />
                    <span style={{ padding: '8px 8px', color: C.grey, fontSize: 12 }}>/hr</span>
                  </div>
                  {isLow && (
                    <div style={{ fontSize: 11, color: C.warning, marginTop: 4 }}>⚠️ Below recommended</div>
                  )}
                </div>

                {/* Monthly cost estimate */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.dark }}>${monthlyCost}</div>
                  <div style={{ fontSize: 11, color: C.grey }}>{sessions} sessions/mo</div>
                </div>

                {/* Reset button */}
                <div style={{ textAlign: 'right' }}>
                  {isCustom && (
                    <button onClick={() => handleReset(key)} disabled={resetting[key]}
                      title="Reset to platform default"
                      style={{ background: 'none', border: `1px solid ${C.border}`, color: C.grey, padding: '5px 10px', borderRadius: 8, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {resetting[key] ? '…' : '↩ Reset'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Save / Reset all */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <button onClick={handleResetAll}
            style={{ background: 'none', border: `1px solid ${C.border}`, color: C.grey, padding: '10px 18px', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            ↩ Reset All to Defaults
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {saved && <span style={{ color: C.success, fontSize: 13, fontWeight: 600 }}>✅ Rates saved</span>}
            {error && <span style={{ color: C.danger, fontSize: 13 }}>{error}</span>}
            <button onClick={handleSave} disabled={saving}
              style={{ background: C.teal, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save Rates'}
            </button>
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginTop: 28, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '14px 18px', fontSize: 13, color: '#92400E' }}>
          <strong>📌 Note:</strong> The monthly cost estimate is based on typical sessions per month for each lesson type.
          Actual costs depend on how many active students you have and how many sessions they attend.
          Tutor platform fees (if applicable) are deducted from these gross rates before tutors receive their net pay.
        </div>

      </div>
    </Layout>
  );
}
